import { useMemo, useRef, useState, useEffect, useCallback, type CSSProperties } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { Project, GridColumn, Person } from '../types';
import { useT } from '../i18n';
import { isPlanningOrExecutionStage } from '../utils/stages';
import { getPersonAllocationBlocks, type PersonAllocationBlock } from '../utils/resourceTimeline';
import { roleDisplay } from '../proposals/roles-projet/projectRoles';
import { parseYMD } from '../utils/echeances';
import { buildMonthRange, dateOffsetPx, monthKey, packLanes, type MonthSlot } from '../utils/monthGrid';

// Cross-person view for Gérance: instead of one project-per-row (the
// calendar tab) or one person's own workload (the resources tab's default
// mode), this lays out every active collaborator's hour allocations side by
// side — detached from any single project — so the PMO can spot who has
// capacity to pick up new work and when someone currently loaded frees up.
// Each bar is a project a person is allocated hours on; its timeframe is the
// project's own best-available span (see resourceTimeline.ts), not a
// milestone, since scoping allocations carry a %/role but no dates.

const MONTH_W = 330;
const LABEL_W = 225;
const MONTHS_BEFORE = 3;
const MONTHS_AFTER = 8;
const MIN_BAR_W = 64;
// Bar height is strictly proportional to hoursPerWeek — 1h/week is
// PX_PER_HOUR (tall enough to read on its own), and 10h/week is exactly 10×
// that, so heights can be compared like a bar chart rather than just an
// eyeballed "taller = more". A hard cap guards only against bad data (e.g. a
// mistyped 100% allocation) blowing up a row's height — real allocations
// top out around 10h/week per the PMO's own numbers.
const PX_PER_HOUR = 8;
const SAFETY_MAX_HOURS_PER_WEEK = 24;
// Lanes are packed to their own content height (see laneLayout below), not a
// fixed slot — a fixed slot sized for the rare tall bar left a huge gap
// above/below every short one. LANE_GAP is the only thing that stays
// constant: the space between two stacked projects always reads the same,
// regardless of either one's height.
const LANE_GAP = 6;

function barHeightFor(hoursPerWeek: number): number {
  const clamped = Math.max(0, Math.min(hoursPerWeek, SAFETY_MAX_HOURS_PER_WEEK));
  return Math.round(clamped * PX_PER_HOUR);
}

// Positions each lane directly beneath the previous one, sized to that
// lane's own tallest bar plus a fixed gap — so the visual gap between any two
// vertically-stacked bars is always LANE_GAP, never stretched to fill a
// uniform slot sized for a taller bar elsewhere in the row.
function laneLayout(bars: { lane: number; hoursPerWeek: number }[]): { tops: number[]; heights: number[]; trackHeight: number } {
  const heights: number[] = [];
  for (const b of bars) heights[b.lane] = Math.max(heights[b.lane] ?? 0, barHeightFor(b.hoursPerWeek));
  const tops: number[] = [];
  let cursor = LANE_GAP;
  for (let lane = 0; lane < heights.length; lane++) {
    tops[lane] = cursor;
    cursor += heights[lane] + LANE_GAP;
  }
  return { tops, heights, trackHeight: Math.max(56, cursor) };
}

function weekTick(x: number): string {
  return `transparent ${x}px, var(--line) ${x}px, var(--line) ${x + 1}px, transparent ${x + 1}px,`;
}
const TRACK_BG = `repeating-linear-gradient(to right,
  var(--line-strong) 0, var(--line-strong) 1px, transparent 1px,
  ${weekTick(MONTH_W / 4)}
  ${weekTick(MONTH_W / 2)}
  ${weekTick((MONTH_W / 4) * 3)}
  transparent ${MONTH_W}px)`;

const MONTH_KEYS = [
  'calendar.month.jan', 'calendar.month.feb', 'calendar.month.mar', 'calendar.month.apr',
  'calendar.month.may', 'calendar.month.jun', 'calendar.month.jul', 'calendar.month.aug',
  'calendar.month.sep', 'calendar.month.oct', 'calendar.month.nov', 'calendar.month.dec',
];

function goToProject(id: number) {
  window.location.hash = `project?id=${id}`;
}

const LABEL_TODAY_GAP = 6;

// For a bar whose span covers today, the project/hours label defaults to the
// bar's own start — which, for an allocation that started months ago, is
// scrolled off-screen and never seen. Anchoring it just left of the today
// line instead keeps it visible wherever the timeline happens to be
// scrolled. Bars today falls outside of (past or not-yet-started) keep the
// default start-anchored label — nothing to fix there, since the bar's own
// start is what's relevant to read.
function labelStyleFor(barLeft: number, barWidth: number, todayLeft: number | null): CSSProperties {
  if (todayLeft == null) return {};
  const relativeToday = todayLeft - barLeft;
  if (relativeToday <= 0 || relativeToday >= barWidth) return {};
  const rightEdgeX = Math.max(0, Math.min(relativeToday - LABEL_TODAY_GAP, barWidth));
  return { position: 'absolute', left: 'auto', right: barWidth - rightEdgeX, maxWidth: Math.max(20, rightEdgeX - 6) };
}

interface Bar extends PersonAllocationBlock {
  left: number;
  width: number;
}

interface PersonRow {
  person: Person;
  bars: (Bar & { lane: number })[];
  laneTops: number[];
  laneHeights: number[];
  trackHeight: number;
  currentHours: number;
}

// One draggable person row — a grip handle (not the row itself) carries the
// drag listeners, both so bar clicks (goToProject) stay clickable and so this
// vertical drag doesn't fight the timeline's own horizontal pan-to-scroll,
// which listens for mousedown across the whole scrollable area (see the
// '.rescmp-drag-handle' guard in the pan effect below).
function SortablePersonRow({ row, totalWidth, todayLeft }: {
  row: PersonRow;
  totalWidth: number;
  todayLeft: number | null;
}) {
  const { t } = useT();
  const { person, bars, laneTops, laneHeights, trackHeight, currentHours } = row;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: person.id });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="cal-row-group rescmp-card">
      <div className="cal-row">
        <div className="cal-row-label" style={{ width: LABEL_W }}>
          <button type="button" className="rescmp-drag-handle" title={t('resources.compareDragReorder')} {...attributes} {...listeners}>
            <GripVertical size={14} />
          </button>
          <div className="cal-row-label-main">
            <div className="cal-row-title" title={person.name}>{person.name}</div>
            <div className="cal-row-sponsor">
              {currentHours > 0
                ? t('resources.compareCurrentHours', { hours: currentHours.toFixed(1) })
                : t('resources.compareCurrentlyFree')}
            </div>
          </div>
        </div>
        <div
          className="cal-row-track"
          style={{
            width: totalWidth,
            backgroundImage: TRACK_BG,
            minHeight: trackHeight,
          }}
        >
          {bars.map((bar, i) => {
            const barH = barHeightFor(bar.hoursPerWeek);
            const laneTop = laneTops[bar.lane];
            const laneH = laneHeights[bar.lane];
            return (
              <div
                key={`${bar.projectId}-${i}`}
                className="rescmp-bar"
                style={{ left: bar.left, width: bar.width, height: barH, top: laneTop + (laneH - barH) / 2 }}
                title={`${bar.projectName}${roleDisplay(t, bar.projectRole, bar.role).text ? ` · ${roleDisplay(t, bar.projectRole, bar.role).text}` : ''} · ${t('resources.colHoursPerWeek')}: ${bar.hoursPerWeek.toFixed(1)} · ${bar.start} → ${bar.end}`}
                onClick={() => goToProject(bar.projectId)}
              >
                <span className="rescmp-bar-label" style={labelStyleFor(bar.left, bar.width, todayLeft)}>
                  {bar.projectName}{roleDisplay(t, bar.projectRole, bar.role).text ? ` · ${roleDisplay(t, bar.projectRole, bar.role).text}` : ''} · {bar.hoursPerWeek.toFixed(1)} h
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function GeranceResourceCompareView({ projects, pipelineColumns, people, projectDeadlines }: {
  projects: Project[];
  pipelineColumns: GridColumn[];
  people: Person[];
  projectDeadlines: Map<number, string>;
}) {
  const { t } = useT();
  const scrollRef = useRef<HTMLDivElement>(null);

  const months = useMemo<MonthSlot[]>(() => buildMonthRange(MONTHS_BEFORE, MONTHS_AFTER), []);
  const totalWidth = months.length * MONTH_W;

  const activeProjects = useMemo(
    () => projects.filter(p => isPlanningOrExecutionStage(p.status_id, pipelineColumns)),
    [projects, pipelineColumns]
  );

  const todayMs = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  }, []);

  const rows = useMemo(() => {
    const built = people.map(person => {
      const blocks = getPersonAllocationBlocks(person.name, activeProjects, projectDeadlines);

      const positioned: Bar[] = blocks.map(b => {
        const left = dateOffsetPx(b.start, months, MONTH_W) ?? 0;
        let right = dateOffsetPx(b.end, months, MONTH_W) ?? left;
        if (right < left) right = left;
        if (right - left < MIN_BAR_W) right = left + MIN_BAR_W;
        return { ...b, left, width: right - left };
      });

      const bars = packLanes(positioned);
      const { tops: laneTops, heights: laneHeights, trackHeight } = laneLayout(bars);
      const currentHours = blocks
        .filter(b => parseYMD(b.start) <= todayMs && todayMs <= parseYMD(b.end))
        .reduce((sum, b) => sum + b.hoursPerWeek, 0);

      return { person, bars, laneTops, laneHeights, trackHeight, currentHours };
    });

    // Default order: busiest person first (most hours occupied right now) —
    // the whole point of this view is spotting who has capacity, so that
    // should read top-to-bottom without needing to drag anyone. Ties (most
    // often two people both currently free) fall back to name so the order
    // stays stable and predictable rather than arbitrary.
    return built.filter(r => r.bars.length > 0).sort((a, b) => b.currentHours - a.currentHours || a.person.name.localeCompare(b.person.name));
  }, [people, activeProjects, projectDeadlines, months, todayMs]);

  // User-set order, layered on top of the freshly-recomputed `rows` — `rows`
  // itself is rebuilt (and re-sorted alphabetically) on every data reload, so
  // the manual order has to be kept separately and re-applied, with anyone
  // not yet in it (new to the active portfolio) falling back to alpha order.
  const [order, setOrder] = useState<number[]>([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const orderedRows = useMemo(() => {
    const byId = new Map(rows.map(r => [r.person.id, r]));
    const known = order.map(id => byId.get(id)).filter((r): r is PersonRow => r != null);
    const rest = rows.filter(r => !order.includes(r.person.id));
    return [...known, ...rest];
  }, [rows, order]);

  const onDragEnd = useCallback((e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = orderedRows.map(r => r.person.id);
    const oldIndex = ids.indexOf(active.id as number);
    const newIndex = ids.indexOf(over.id as number);
    if (oldIndex < 0 || newIndex < 0) return;
    setOrder(arrayMove(ids, oldIndex, newIndex));
  }, [orderedRows]);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const todayLeft = dateOffsetPx(todayStr, months, MONTH_W);
  const todayMonthIdx = months.findIndex(m => m.year === today.getFullYear() && m.month === today.getMonth());

  const jumpToday = useCallback(() => {
    if (todayLeft == null || !scrollRef.current) return;
    scrollRef.current.scrollLeft = Math.max(0, LABEL_W + todayLeft - 2 * MONTH_W);
  }, [todayLeft]);

  useEffect(() => { jumpToday(); }, [jumpToday]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    const DRAG_THRESHOLD = 4;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      // Row reordering (dnd-kit, via the grip handle) also starts on
      // mousedown within this same scrollable area — let it own the
      // gesture instead of also kicking off a horizontal pan.
      if ((e.target as HTMLElement).closest('.rescmp-drag-handle')) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
        moved = true;
        el.classList.add('is-dragging');
      }
      if (moved) {
        el.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      if (dragging && moved) {
        const suppressClick = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
        el.addEventListener('click', suppressClick, { capture: true, once: true });
        e.preventDefault();
      }
      dragging = false;
      moved = false;
      el.classList.remove('is-dragging');
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">{t('resources.compareEmptyTitle')}</div>
        <div className="empty-state-desc">{t('resources.compareEmptyDesc')}</div>
      </div>
    );
  }

  return (
    <div className="cal-page">
      <div className="tl-legend">
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={jumpToday}>
          {t('planning.jumpToday')}
        </button>
      </div>

      <div className="cal-scroll" ref={scrollRef}>
        <div className="cal-inner" style={{ width: LABEL_W + totalWidth }}>
          <div className="cal-header">
            <div className="cal-corner" style={{ width: LABEL_W }} />
            {months.map((m) => (
              <div
                key={monthKey(m)}
                className={`cal-month-head${todayMonthIdx >= 0 && monthKey(m) === monthKey(months[todayMonthIdx]) ? ' is-today' : ''}`}
                style={{ width: MONTH_W }}
              >
                {t(MONTH_KEYS[m.month])} {m.year}
              </div>
            ))}
          </div>

          <div className="cal-body rescmp-body">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={orderedRows.map(r => r.person.id)} strategy={verticalListSortingStrategy}>
                {orderedRows.map(row => (
                  <SortablePersonRow key={row.person.id} row={row} totalWidth={totalWidth} todayLeft={todayLeft} />
                ))}
              </SortableContext>
            </DndContext>
            {todayLeft != null && (
              <div className="cal-today-line" style={{ left: LABEL_W + todayLeft }} title={t('planning.today')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
